import { Sparkles } from "lucide-react";

import { Button } from "../../ui/button";
import {
  DASHBOARD_PRESETS,
  PRESET_DESCRIPTIONS,
  PRESET_NAMES,
  type DashboardLayout,
  type PresetName,
} from "./types";

export type EmptyDashboardStateProps = {
  onApplyPreset: (layout: DashboardLayout) => void;
  onStartBlank: () => void;
};

export function EmptyDashboardState({
  onApplyPreset,
  onStartBlank,
}: EmptyDashboardStateProps) {
  return (
    <section
      aria-label="Empty dashboard"
      className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-16"
    >
      <DashboardIllustration />
      <h2 className="mt-6 font-display text-3xl font-medium tracking-tight text-on-surface">
        Your dashboard is empty
      </h2>
      <p className="mt-2 max-w-md text-center text-sm text-on-surface-variant">
        Pick a starter preset that matches how you trade, or start blank
        and add widgets one at a time.
      </p>

      <div className="mt-8 grid w-full max-w-3xl gap-3 sm:grid-cols-3">
        {PRESET_NAMES.map((name) => (
          <PresetCard
            key={name}
            name={name}
            onSelect={() => {
              onApplyPreset(DASHBOARD_PRESETS[name]);
            }}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onStartBlank}
        className="mt-6 inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:underline"
      >
        Start blank
      </button>
    </section>
  );
}

function PresetCard({
  name,
  onSelect,
}: {
  name: PresetName;
  onSelect: () => void;
}) {
  const layout = DASHBOARD_PRESETS[name];
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onSelect}
      className="flex h-auto flex-col items-stretch gap-3 whitespace-normal break-words p-4 text-left transition-colors hover:border-primary"
      aria-label={`Apply ${name} preset`}
    >
      <PresetThumbnail name={name} />
      <div className="min-w-0">
        <div className="font-display text-base font-medium text-on-surface">
          {name}
        </div>
        <div className="mt-1 whitespace-normal break-words text-xs font-normal leading-snug text-on-surface-variant">
          {PRESET_DESCRIPTIONS[name]}
        </div>
        <div className="mt-2 text-[10px] font-medium uppercase tracking-wide text-on-surface-muted">
          {layout.widgets.length} widgets
        </div>
      </div>
    </Button>
  );
}

// Tiny grid silhouette echoing the preset's actual layout — uses each
// widget's x/y/w/h directly against a 12×N viewBox so the thumbnail
// matches the real grid the user is about to apply.
function PresetThumbnail({ name }: { name: PresetName }) {
  const layout = DASHBOARD_PRESETS[name];
  const maxY = layout.widgets.reduce((acc, w) => Math.max(acc, w.y + w.h), 1);
  const cellW = 8;
  const cellH = 8;
  const padding = 2;
  const vbW = 12 * cellW + padding * 2;
  const vbH = maxY * cellH + padding * 2;
  return (
    <div className="flex h-20 w-full items-center justify-center rounded border border-outline-variant bg-surface">
      <svg
        viewBox={`0 0 ${vbW} ${vbH}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        {layout.widgets.map((w) => (
          <rect
            key={w.id}
            x={padding + w.x * cellW + 1}
            y={padding + w.y * cellH + 1}
            width={w.w * cellW - 2}
            height={w.h * cellH - 2}
            rx={1.5}
            fill="currentColor"
            opacity={w.type.startsWith("kpi_") ? 0.45 : w.type.startsWith("chart_") ? 0.32 : 0.22}
          />
        ))}
      </svg>
    </div>
  );
}

function DashboardIllustration() {
  return (
    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-surface-container-high text-primary">
      <Sparkles className="h-10 w-10" aria-hidden="true" />
    </div>
  );
}
